/* ============================================================
   sl-hotspot.js — hotspot-by studiolab · state controller + connector engine
   VERSION: 1.12.0  ·  2O3 (Concept)
   1.12.0 — Mobile composition follows schema dimensions in natural flow. A separate
            viewport pins the scene; each gesture reads content before changing
            at most one phase. Editor/manual navigation retains the same introduction.
   1.11.0 — Optional CTA footer is excluded from the scroll runway. Geometry
            follows upstream layout shifts and responsive/editor reinitialization.
   1.10.0 — Optional mobile gesture lock limits a sticky wheel/touch gesture to
            one feature while preserving natural entry, exit and inner text scroll.
   1.9.0 — Crossfades wait for the target image to decode; mobile scroll begins on
           a dedicated neutral/default-image phase; navigation is finite; connector
           drawing can be disabled without disabling the callouts.
   1.8.0 — Active feature images own independent desktop/mobile heights and aspect
           ratios. Blank feature media restores the section image geometry; the
           sizing state also re-syncs across responsive breakpoint changes.
   1.7.0 — Image sizing is fully editor-owned. Removed the mobile viewport cap and
           observe the absolute image box so connectors keep tracking every size.
   1.6.1 — Compositor-stable mobile pin + cached geometry. Scroll-driven feature
           changes use a short, interruptible slide and settle to the final connector
           instead of replaying the long stroke animation on every interval.
   1.6.0 — Mobile natural-scroll carousel scene:
           · A sticky, viewport-fitted scene maps each scroll interval to one feature.
           · Arrows update the page position instead of fighting the scroll state.
           · Theme Editor and prefers-reduced-motion retain the ordinary arrow slider.
   1.5.1 — Theme Editor + accessibility refinement:
           · Disabled/editor reveals settle immediately, so connectors never wait
             on hidden animation timings after a setting change.
           · Accordion triggers are native buttons; reduced-motion is read live;
             teardown clears every pending connector timer.
   1.5.0 — Staged overhaul (designed via /grill-me):
           · Mobile-line fix: redraw the connector once after the entrance reveal
             settles (the reveal is a CSS transition the ResizeObserver can't see, so
             the from-load mobile line could point off into space until first click).
           · "Somente hotspots" reverse mode (config.mode='hotspots'): callouts hidden
             by default; the visible per-feature dots are the triggers; clicking one
             draws the line dot→callout (buildRoutedPath `reverse` flips the path's M)
             and the callout fades in on arrival (.is-shown) instead of the dot
             (.is-arrived). Collapsed on both breakpoints (syncBreakpoint); mobile uses
             the per-feature dot as the endpoint, not the terminus.
           · First-callout-open-on-reveal (config.defaultOpen, standard+desktop only):
             maybeDefaultOpen opens feature 0 after the entrance settles.
   1.4.0 — Connector engine rewritten as ONE orthogonal auto-wrap router
           (buildRoutedPath) for both layouts. Per-feature, per-device EXIT side
           (top/bottom/left/right) + offset along that edge (data-side/data-offset,
           resolved+fallback in Liquid). The whole card box + a section-level
           clearance (config.lineClearance) is a hard no-enter zone; the line steps
           out by the clearance then routes in right angles to the hotspot, wrapping
           around the card when the side faces away. Removed buildDesktopPath, the
           mobile left/bottom branch, data-dir-x/y, data-m-start, lineShape/lineAnchor.
   1.1.1 — 'title' anchor → title's inner edge at mid-height (line never crosses the
           callout's own text); connector re-tracks the title when the accordion
           expands/re-centers (ResizeObserver watches columns/body; reposition
           redraws are non-animated so they don't restart the stroke draw-on).
   1.1.2 — Desktop routing rebuilt (buildDesktopPath): the WHOLE callout box (title +
           body) is a forbidden region — the elbow's bend is forced BEYOND the box
           edge on the dot's side, so the line routes AROUND the content (never
           through it) and adapts automatically to any title size / amount of text.
   1.3.0 — Per-feature MOBILE line start (data-m-start: 'left' default | 'bottom').
           'bottom' exits the card's bottom-centre and drops into the hotspot;
           Direção X/Y (data-dir-x/y) still nudge the trajectory. Desktop unchanged.
           NB: 'bottom' start-X uses the STAGE centre (slide-stable), not the card's
           own rect — cards live in a translateX slider, so a card's X is offset by
           the slide / wrong mid-transition; the active card always settles centred.

   PUBLIC SURFACE (SPEC §8, followed literally):
     window.slHotspot.init(sectionId, config)
     window.slHotspot.activate(sectionId, index)   ← open a feature (exclusive)
     window.slHotspot.next(sectionId) / prev(sectionId)
   PLUS (documented extension — desktop accordion needs collapse-on-reclick):
     window.slHotspot.toggle(sectionId, index)      ← open, or collapse if already open

   The external file is loaded `defer`, but each section emits a NON-defer inline
   bootstrap that calls init() during parse — before this file runs. A queue stub
   (window.slHotspot._q) absorbs those early calls; this file drains them on load.
   init() is idempotent per section.id (Theme-Editor shopify:section:load safe).

   DECISIONS honored:
   - Collapsed on load (active = -1). Editor block-select opens that callout.
   - Exclusive accordion; clicking the open one collapses (toggle()).
   - Snappy connector: repoint + redraw on switch; fade out on collapse.
   - One geometry fn for BOTH layouts — reads real rects relative to the stage.
   - line_anchor: title | box | auto (nearest edge). Elbow or straight.
   - Mobile keeps the line + a non-interactive terminus dot. Hotspot dots desktop-only (CSS).
   - prefers-reduced-motion: final line shown instantly, no draw / jitter.
   - No external library. GSAP is detection-only (collage jitter randomisation).
   ============================================================ */
(function () {
  'use strict';

  /* load-once: multiple sections / editor reloads share one src — never
     re-bind the global document/window listeners below. */
  if (window.__slHotspotLoaded) return;
  window.__slHotspotLoaded = true;

  /* ---- adopt any queued calls from the inline bootstrap stub ---------- */
  var queued = (window.slHotspot && window.slHotspot._q) || [];

  var DESKTOP_MQ = '(min-width: 1024px)';

  /* instances keyed by sectionId, so re-init can tear down cleanly */
  var instances = {};
  var scrollRafPending = false;

  /* ---------- helpers -------------------------------------------------- */
  function root(sectionId) { return document.getElementById('sl-section-' + sectionId); }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function scrollableDescendantCanConsume(target, boundary, direction) {
    var node = target && target.nodeType === 1 ? target : target && target.parentElement;
    while (node && node !== boundary) {
      var style = window.getComputedStyle(node);
      if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1) {
        if (direction > 0 && node.scrollTop + node.clientHeight < node.scrollHeight - 1) return true;
        if (direction < 0 && node.scrollTop > 1) return true;
      }
      node = node.parentElement;
    }
    return false;
  }

  function hasMobileIntro(inst) {
    return inst.root.getAttribute('data-scroll-mobile') === 'true'
      && !window.matchMedia(DESKTOP_MQ).matches;
  }

  function mobileScrollEnabled(inst) {
    return !!(
      inst
      && !inst.inEditor
      && inst.count > 1
      && inst.root.getAttribute('data-scroll-mobile') === 'true'
      && !window.matchMedia(DESKTOP_MQ).matches
      && !prefersReducedMotion()
    );
  }

  function clearMobilePin(inst) {
    if (!inst || !inst.root) return;
    inst.root.classList.remove('is-scroll-pinned', 'is-scroll-past');
    inst.scrollMetrics = null;
    inst.scrollPhase = null;
    if (inst.scene) {
      inst.scene.style.removeProperty('left');
      inst.scene.style.removeProperty('width');
      inst.scene.style.removeProperty('transform');
      inst.scene.scrollTop = 0;
    }
  }

  function syncScrollModeClass(inst) {
    var enabled = mobileScrollEnabled(inst);
    // Manual navigation applies only when scroll retention is disabled.
    inst.root.classList.toggle('is-compact-mobile', !inst.inEditor
      && inst.config.mode === 'standard'
      && !window.matchMedia(DESKTOP_MQ).matches && !enabled);
    if (inst.root.classList.contains('is-scroll-ready') !== enabled) {
      inst.root.classList.toggle('is-scroll-ready', enabled);
    }
    return enabled;
  }

  function syncFooterHeight(inst) {
    var shopSpace = 0;
    document.querySelectorAll('product-sticky-form[data-show-outside-section]').forEach(function (form) {
      if (getComputedStyle(form).position !== 'fixed') return;
      var height = form.getBoundingClientRect().height;
      if (height > 0) shopSpace = Math.max(shopSpace, height + (parseFloat(getComputedStyle(form).bottom) || 0));
    });
    var shopValue = shopSpace + 'px';
    if (inst.root.style.getPropertyValue('--sl-hotspot-shop-space') !== shopValue) {
      inst.root.style.setProperty('--sl-hotspot-shop-space', shopValue);
    }
    if (!inst.footer) return;
    var height = inst.footer.getBoundingClientRect().height;
    var value = height + 'px';
    if (inst.root.style.getPropertyValue('--sl-hotspot-footer-height') !== value) {
      inst.root.style.setProperty('--sl-hotspot-footer-height', value);
    }
  }

  function measureMobileScroll(inst) {
    var scene = inst.scene || inst.root.querySelector('[data-hotspot-scene]');
    if (!scene) return null;
    var rootRect = inst.root.getBoundingClientRect();
    var sceneRect = scene.getBoundingClientRect();
    var rootStyles = getComputedStyle(inst.root);
    var stickyTop = parseFloat(rootStyles.getPropertyValue('--sticky-header-height'))
      || parseFloat(rootStyles.getPropertyValue('--header-height'))
      || 0;
    // The optional CTA after the scene must not add another animation phase.
    var footerHeight = inst.footer ? inst.footer.getBoundingClientRect().height : 0;
    var range = Math.max(1, rootRect.height - sceneRect.height - footerHeight);
    inst.scrollMetrics = {
      start: rootRect.top + window.scrollY - stickyTop,
      range: range,
      /* The first phase is the section default image; feature i lives at phase i+1. */
      step: range / (inst.count + 1),
      left: rootRect.left,
      width: rootRect.width
    };
    return inst.scrollMetrics;
  }

  function mobileScrollMetrics(inst) {
    // Upstream responsive sections can move this scene without resizing it.
    return measureMobileScroll(inst);
  }

  function syncScrollDriven(inst) {
    if (!syncScrollModeClass(inst)) {
      /* On desktop there is no scroll-driven scene. Avoid rewriting the same
         classes and inline styles on every document scroll frame. */
      if (
        inst.scrollMetrics !== null
        || inst.scrollPhase !== null
        || inst.root.classList.contains('is-scroll-pinned')
        || inst.root.classList.contains('is-scroll-past')
      ) clearMobilePin(inst);
      return;
    }
    var metrics = mobileScrollMetrics(inst);
    if (!metrics) return;
    var offset = window.scrollY - metrics.start;
    var pinned = offset >= 0 && offset < metrics.range;
    var past = offset >= metrics.range;
    var phase = pinned ? 'pinned' : (past ? 'past' : 'before');
    if (inst.scrollPhase !== phase) {
      inst.root.classList.toggle('is-scroll-pinned', pinned);
      inst.root.classList.toggle('is-scroll-past', past);
      if (inst.scene) {
        if (pinned) {
          inst.scene.style.left = metrics.left + 'px';
          inst.scene.style.width = metrics.width + 'px';
        } else {
          inst.scene.style.removeProperty('left');
          inst.scene.style.removeProperty('width');
        }
      }
      inst.scrollPhase = phase;
    }
    var travelled = Math.max(0, Math.min(metrics.range - 0.01, offset));
    var phaseIndex = Math.min(inst.count, Math.floor(travelled / metrics.step));
    var index = phaseIndex - 1;
    if (inst.active !== index) setActive(inst, index, true);
  }

  function queueScrollSync() {
    /* Desktop Hotspot has no scroll-bound behavior. Skipping the global rAF
       here removes needless style recalculation across the whole storefront. */
    if (window.matchMedia(DESKTOP_MQ).matches) return;
    if (scrollRafPending) return;
    scrollRafPending = true;
    requestAnimationFrame(function () {
      scrollRafPending = false;
      for (var id in instances) {
        if (instances.hasOwnProperty(id)) syncScrollDriven(instances[id]);
      }
    });
  }

  function scrollToFeature(inst, index) {
    index = Math.max(-1, Math.min(inst.count - 1, index));
    if (inst.scene) inst.scene.scrollTop = 0;
    if (!mobileScrollEnabled(inst)) {
      setActive(inst, index, true);
      return;
    }
    var metrics = mobileScrollMetrics(inst);
    if (!metrics) return;
    var target = metrics.start + ((index + 1.5) * metrics.step);
    // The scene is fixed; animate its cards, not a second native page scroll
    // competing with the finger/trackpad. Center the phase away from its edges.
    window.scrollTo({ top: target, behavior: 'instant' });
    syncScrollDriven(inst);
  }

  function bindMobileStepLock(inst) {
    var scene = inst.scene;
    if (!scene || inst.root.getAttribute('data-scroll-step-lock') !== 'true') return;
    inst.wheelLocked = false;
    inst.wheelTimer = 0;
    inst.touchGesture = null;
    var readingCarry = 0;

    function entryIndex(direction) {
      if (!mobileScrollEnabled(inst)) return null;
      var metrics = mobileScrollMetrics(inst);
      if (!metrics) return null;
      var offset = window.scrollY - metrics.start;
      if (direction > 0 && offset < 0) return -1;
      if (direction < 0 && offset >= metrics.range) return inst.count - 1;
      return null;
    }

    function canStep(direction) {
      return entryIndex(direction) !== null || (mobileScrollEnabled(inst)
        && inst.root.classList.contains('is-scroll-pinned'));
    }

    function consumeReading(delta) {
      if (!inst.root.classList.contains('is-scroll-pinned')) {
        readingCarry = 0;
        return delta;
      }
      var max = Math.max(0, scene.scrollHeight - scene.clientHeight);
      var current = Math.max(0, Math.min(max, scene.scrollTop));
      var remaining = delta > 0 ? max - current : current;
      // Layout rounds scrollHeight/clientHeight but scrollTop can be fractional.
      // Treat the final 2px as the boundary, including Safari's elastic offset.
      if (remaining <= 2) {
        scene.scrollTop = delta > 0 ? max : 0;
        readingCarry = 0;
        return delta;
      }
      var total = delta + readingCarry;
      var target = Math.max(0, Math.min(max, current + total));
      scene.scrollTop = target;
      // Browsers can round scrollTop writes to physical pixels. Keep the lost
      // fraction so a stream of tiny deltas still moves through the content.
      readingCarry = target - Math.max(0, Math.min(max, scene.scrollTop));
      return total - (target - current);
    }

    function requestStep(direction) {
      var entry = entryIndex(direction);
      var target = entry !== null ? entry : inst.active + direction;
      if (target < -1 || target >= inst.count) {
        // Leaving is a separate gesture too; consume its remaining momentum.
        var metrics = mobileScrollMetrics(inst);
        window.scrollTo({ top: metrics.start + (direction > 0 ? metrics.range + 1 : -1), behavior: 'instant' });
        syncScrollDriven(inst);
      } else {
        scrollToFeature(inst, target);
      }
      return true;
    }

    inst.onStepWheel = function (event) {
      if (event.defaultPrevented || event.shiftKey || !mobileScrollEnabled(inst)) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      var delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scene.clientHeight : 1);
      var direction = event.deltaY > 0 ? 1 : -1;
      if (!inst.wheelLocked && !canStep(direction)) return;
      if (scrollableDescendantCanConsume(event.target, scene, direction)) return;
      event.preventDefault();
      clearTimeout(inst.wheelTimer);
      if (inst.wheelLocked && inst.wheelTriggered) {
        if (direction !== inst.wheelDirection) {
          inst.wheelReverseDistance += delta;
          // Opposite movement is a new intent, even while the previous wheel
          // burst is settling. Filter tiny momentum rebounds, not a real return.
          if (Math.abs(inst.wheelReverseDistance) >= 24) {
            delta = inst.wheelReverseDistance;
            inst.wheelLocked = false;
          }
        } else {
          inst.wheelReverseDistance = 0;
        }
      }
      if (!inst.wheelLocked) {
        inst.wheelLocked = true;
        inst.wheelDistance = 0;
        inst.wheelTriggered = false;
        inst.wheelReverseDistance = 0;
        readingCarry = 0;
      }
      if (!inst.wheelTriggered) {
        var remainder = consumeReading(delta);
        // Read all content first. Continuing beyond its boundary can advance
        // one feature in this same gesture; only a second advance is locked.
        if (Math.abs(remainder) < Math.abs(delta)) inst.wheelDistance = 0;
        inst.wheelDistance += remainder;
        if (Math.abs(inst.wheelDistance) >= 24) {
          inst.wheelDirection = inst.wheelDistance > 0 ? 1 : -1;
          inst.wheelTriggered = requestStep(inst.wheelDirection);
        }
      }
      // Keep a continuous momentum stream together without swallowing the next
      // separate swipe. A deliberate direction reversal is handled immediately.
      inst.wheelTimer = setTimeout(function () { inst.wheelLocked = false; }, 180);
    };

    inst.onStepTouchStart = function (event) {
      if (event.touches.length !== 1) { inst.touchGesture = null; return; }
      readingCarry = 0;
      inst.touchGesture = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
        lastY: event.touches[0].clientY,
        axis: null,
        distance: 0,
        triggered: false
      };
    };
    inst.onStepTouchMove = function (event) {
      var gesture = inst.touchGesture;
      if (!gesture || event.touches.length !== 1 || !mobileScrollEnabled(inst)) return;
      var y = event.touches[0].clientY;
      var deltaX = gesture.x - event.touches[0].clientX;
      var deltaY = gesture.y - y;
      var movement = gesture.lastY - y;
      var direction = deltaY > 0 ? 1 : -1;
      if (!gesture.axis) {
        if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) {
          if (deltaY && canStep(direction)) event.preventDefault();
          return;
        }
        gesture.axis = Math.abs(deltaY) > Math.abs(deltaX) ? 'y' : 'x';
      }
      if (gesture.axis !== 'y') return;
      if (gesture.triggered) { event.preventDefault(); return; }
      if (scrollableDescendantCanConsume(event.target, scene, movement > 0 ? 1 : -1)) return;
      if (!canStep(movement > 0 ? 1 : -1)) return;
      event.preventDefault();
      gesture.lastY = y;
      var remainder = consumeReading(movement);
      if (Math.abs(remainder) < Math.abs(movement)) gesture.distance = 0;
      gesture.distance += remainder;
      if (Math.abs(gesture.distance) >= 24) {
        gesture.triggered = requestStep(gesture.distance > 0 ? 1 : -1);
      }
    };
    inst.onStepTouchEnd = function () {
      inst.touchGesture = null;
    };

    scene.addEventListener('wheel', inst.onStepWheel, { passive: false });
    scene.addEventListener('touchstart', inst.onStepTouchStart, { passive: true });
    scene.addEventListener('touchmove', inst.onStepTouchMove, { passive: false });
    scene.addEventListener('touchend', inst.onStepTouchEnd, { passive: true });
    scene.addEventListener('touchcancel', inst.onStepTouchEnd, { passive: true });
  }

  function visibleStage(rootEl) {
    /* the layout whose stage is actually rendered at this breakpoint */
    var stages = rootEl.querySelectorAll('[data-hotspot-stage]');
    for (var i = 0; i < stages.length; i++) {
      if (stages[i].offsetParent !== null || stages[i].getClientRects().length) return stages[i];
    }
    return stages[0] || null;
  }

  function stageLayout(stage) {
    return stage && stage.getAttribute('data-hotspot-stage') === 'mobile' ? 'mobile' : 'desktop';
  }

  /* ---------- unified connector router (both layouts) --------------------
     The WHOLE card box (title + body) is a forbidden region — the line must
     never enter it. The line LEAVES the card from a chosen side at an offset
     along that edge, steps out by `clearance` (the magnet gap), then routes
     orthogonally (right angles only) to the hotspot, WRAPPING around the card
     when the chosen side faces away from the hotspot. One rectangle obstacle,
     so the routing is closed-form. All coords are STAGE pixel space.

       side   : 'top' | 'bottom' | 'left' | 'right'  — the edge the line exits
       offset : 0..100 — position along that edge
                 top/bottom → 0=left .. 100=right ; left/right → 0=top .. 100=bottom
       dot    : { x, y } hotspot centre (stage space)
       clearance : px the line keeps off the card (padded forbidden box)

     Guarantee: the hotspot lives on the image (beside/below the card), so it is
     always outside the padded box; every segment below is constructed to stay on
     or outside the padded box edges, so the line never crosses the card. */
  function buildRoutedPath(cardRect, stageRect, dot, side, offset, clearance, reverse) {
    var c = clearance == null ? 16 : clearance;
    var L = cardRect.left - stageRect.left, R = cardRect.right - stageRect.left;
    var T = cardRect.top - stageRect.top,  B = cardRect.bottom - stageRect.top;
    var w = R - L, h = B - T;
    var cx = (L + R) / 2, cy = (T + B) / 2;
    var PL = L - c, PR = R + c, PT = T - c, PB = B + c;   /* padded (forbidden) box */
    var o = Math.max(0, Math.min(100, isNaN(offset) ? 50 : offset)) / 100;
    var t = { x: dot.x, y: dot.y };
    var pts;

    if (side === 'left' || side === 'right') {
      var ey = T + o * h;                          /* point along the vertical edge */
      var ex = side === 'right' ? R : L;           /* card edge */
      var e2x = side === 'right' ? PR : PL;        /* padded edge (clearance out) */
      var inFront = side === 'right' ? (t.x >= PR) : (t.x <= PL);
      if (inFront) {                               /* hotspot is in front of the exit → simple L */
        pts = [[ex, ey], [e2x, ey], [t.x, ey], [t.x, t.y]];
      } else {                                     /* faces away → wrap over the nearer corner */
        var clearY = (t.y < cy) ? PT : PB;
        pts = [[ex, ey], [e2x, ey], [e2x, clearY], [t.x, clearY], [t.x, t.y]];
      }
    } else {                                       /* top | bottom */
      var ex2 = L + o * w;                          /* point along the horizontal edge */
      var ey2 = side === 'bottom' ? B : T;          /* card edge */
      var e2y = side === 'bottom' ? PB : PT;        /* padded edge */
      var inFront2 = side === 'bottom' ? (t.y >= PB) : (t.y <= PT);
      if (inFront2) {
        pts = [[ex2, ey2], [ex2, e2y], [ex2, t.y], [t.x, t.y]];
      } else {
        var clearX = (t.x < cx) ? PL : PR;
        pts = [[ex2, ey2], [ex2, e2y], [clearX, e2y], [clearX, t.y], [t.x, t.y]];
      }
    }

    /* reverse = draw FROM the hotspot TO the callout (Somente hotspots mode): same
       geometry, but the path's M moves to the dot so the stroke-draw reveals dot→card. */
    if (reverse) pts.reverse();
    var d = 'M ' + round(pts[0][0]) + ' ' + round(pts[0][1]);
    for (var i = 1; i < pts.length; i++) d += ' L ' + round(pts[i][0]) + ' ' + round(pts[i][1]);
    return d;
  }
  function round(n) { return Math.round(n * 100) / 100; }

  function hideAllDots(inst) {
    /* a dot is only shown once its line has arrived — clear them all on each
       (re)draw so no dot lingers as a lonely bubble. (In Somente-hotspots mode the
       dots stay visible via CSS; this only clears the .is-arrived reveal class.) */
    var dots = inst.root.querySelectorAll('[data-hotspot-dot], [data-hotspot-terminus]');
    for (var i = 0; i < dots.length; i++) dots[i].classList.remove('is-arrived');
  }

  function hideAllShown(inst) {
    /* Somente-hotspots: callouts/cards are hidden until their line arrives; clear the
       reveal class on each (re)draw so only the active one is shown. */
    var els = inst.root.querySelectorAll('[data-hotspot-callout], [data-hotspot-card]');
    for (var i = 0; i < els.length; i++) els[i].classList.remove('is-shown');
  }

  function drawConnector(inst, stage, index, animate) {
    var svg = stage.querySelector('[data-hotspot-connector]');
    if (!svg) return;
    var path = svg.querySelector('path');
    if (!path) {
      path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('fill', 'none');
      svg.appendChild(path);
    }

    /* hide every dot on each (re)draw; the active one is revealed below once its
       line reaches it (or immediately under reduced-motion). */
    if (inst.dotTimer) { clearTimeout(inst.dotTimer); inst.dotTimer = null; }
    hideAllDots(inst);
    var hotspots = inst.config.mode === 'hotspots';
    if (hotspots) hideAllShown(inst);

    if (!inst.config.showConnectors) {
      svg.style.opacity = '0';
      if (hotspots && index >= 0) {
        var visibleCallout = inst.root.querySelector(
          '[data-hotspot-' + (stageLayout(stage) === 'mobile' ? 'card' : 'callout') + '="' + index + '"]'
        );
        if (visibleCallout) visibleCallout.classList.add('is-shown');
      }
      return;
    }

    if (index < 0) { /* collapsed → fade the line out (callouts already hidden) */
      svg.style.opacity = '0';
      return;
    }

    var layout = stageLayout(stage);
    var selector = '[data-hotspot-' + (layout === 'mobile' ? 'card' : 'callout') + '="' + index + '"]';
    var callout = inst.root.querySelector(selector);
    /* Somente-hotspots uses the per-feature dot as the line endpoint on BOTH layouts
       (the visible, clickable marker); standard mobile uses the single terminus. */
    var dotEl = (layout === 'mobile' && !hotspots)
      ? stage.querySelector('[data-hotspot-terminus]')
      : stage.querySelector('[data-hotspot-dot="' + index + '"]');
    if (!callout || !dotEl) return;

    var stageRect = stage.getBoundingClientRect();
    var dotRect = dotEl.getBoundingClientRect();
    var dot = { x: dotRect.left + dotRect.width / 2 - stageRect.left, y: dotRect.top + dotRect.height / 2 - stageRect.top };

    /* per-feature, per-device EXIT side + offset (resolved + fallen-back in Liquid,
       so the rendered card/callout already carries the EFFECTIVE values for THIS
       layout). The whole card box is the forbidden region; the router wraps around
       it as needed and never crosses it. */
    var side = (callout.getAttribute('data-side') || (layout === 'mobile' ? 'bottom' : 'right')).toLowerCase();
    var offset = parseFloat(callout.getAttribute('data-offset'));
    var d = buildRoutedPath(callout.getBoundingClientRect(), stageRect, dot, side, offset, inst.config.lineClearance, hotspots);
    path.setAttribute('d', d);

    var len = path.getTotalLength();
    path.style.strokeDasharray = len + ' ' + len;
    svg.style.opacity = '1';

    /* on arrival: standard reveals the target DOT; Somente-hotspots reveals the CALLOUT
       (the dot is already visible — it was the trigger). */
    function onArrive() {
      if (hotspots) { if (callout) callout.classList.add('is-shown'); }
      else { dotEl.classList.add('is-arrived'); }
    }

    if (prefersReducedMotion() || !animate) { /* reposition (resize / accordion settle) or
                                            reduced-motion: show the final line + target
                                            instantly, no draw-on (so re-tracking the
                                            moving title never restarts the stroke). */
      path.style.transition = 'none';
      path.style.strokeDashoffset = '0';
      onArrive();
      return;
    }
    /* snappy redraw: jump to undrawn, force reflow, then draw */
    path.style.transition = 'none';
    path.style.strokeDashoffset = len + 'px';
    path.getBoundingClientRect();        /* reflow */
    path.style.transition = 'stroke-dashoffset ' + inst.config.lineSpeed + 'ms ease-in-out';
    path.style.strokeDashoffset = '0';
    /* reveal the target the moment the line reaches it */
    inst.dotTimer = setTimeout(onArrive, inst.config.lineSpeed);
  }

  function redrawActive(inst, animate) {
    if (inst.settling) return;   /* hold every draw until the layout has settled */
    var stage = visibleStage(inst.root);
    if (stage) drawConnector(inst, stage, inst.active, animate);
  }

  /* A user switch animates a layout transition that keeps the target MOVING:
     desktop accordion → the panel expands (grid-template-rows .35s) and the
     vertically-centered column re-centers as it grows, so the active title keeps
     moving; mobile slider → the track slides (transform .4s), so the active card's
     X is off-screen until it lands. Drawing the animated stroke now would aim at
     the pre-transition target and then a ResizeObserver tick would snap it elsewhere
     mid-draw ("the line changes its mind"). So hide the connector during the expand
     and draw ONCE, animated, after the panel settles — the stroke aims true and
     never re-points. A generation token cancels a stale settle if the visitor opens
     another feature first; a timeout backs up the transitionend. */
  function settleThenDraw(inst, index) {
    var gen = (inst.settleGen = (inst.settleGen || 0) + 1);
    inst.settling = true;
    var stage = visibleStage(inst.root);
    var svg = stage && stage.querySelector('[data-hotspot-connector]');
    if (svg) svg.style.opacity = '0';
    if (inst.settleTimer) { clearTimeout(inst.settleTimer); inst.settleTimer = null; }
    /* Watch the transition that is actually moving the geometry: on desktop the
       opening callout's panel (grid-template-rows .35s, the re-centering title);
       on mobile the slider track (transform .4s normally / .28s in the scroll
       scene — a card's X is wrong until the slide finishes, which sent the line
       "from beyond" off-screen). */
    var desktop = window.matchMedia(DESKTOP_MQ).matches;
    var watchEl, prop;
    if (desktop) {
      var callout = inst.root.querySelector('[data-hotspot-callout="' + index + '"]');
      watchEl = callout && callout.querySelector('[class*="__panel"]');
      prop = 'grid-template-rows';
    } else {
      watchEl = inst.root.querySelector('[data-hotspot-track]');
      prop = 'transform';
    }
    function finish() {
      if (watchEl) watchEl.removeEventListener('transitionend', onEnd);  /* always — no leak */
      if (gen !== inst.settleGen) return;            /* superseded: don't touch shared state */
      if (inst.settleTimer) { clearTimeout(inst.settleTimer); inst.settleTimer = null; }
      inst.settling = false;
      redrawActive(inst, !mobileScrollEnabled(inst));
    }
    function onEnd(e) { if (e.target === watchEl && e.propertyName === prop) finish(); }
    if (watchEl) watchEl.addEventListener('transitionend', onEnd);
    inst.settleTimer = setTimeout(finish, mobileScrollEnabled(inst) ? 360 : 480);
  }

  /* mobile is a slider (no collapsed state) — ensure a feature is active there;
     desktop keeps the collapsed-on-load state. Called at init + on resize. */
  function syncBreakpoint(inst) {
    var desktop = window.matchMedia(DESKTOP_MQ).matches;
    syncFeatureImageSize(inst, inst.active);
    if (!syncScrollModeClass(inst)) clearMobilePin(inst);
    inst.scrollMetrics = null;
    inst.scrollPhase = null;
    /* Somente-hotspots: both breakpoints start collapsed — the visible hotspot markers
       are the triggers, so there's no auto-active feature on either layout. */
    if (inst.config.mode === 'hotspots') {
      inst.wasDesktop = desktop;
      redrawActive(inst);
      return;
    }
    if (mobileScrollEnabled(inst)) {
      if (inst.wasDesktop !== false) setActive(inst, -1);
      syncScrollDriven(inst);
      inst.wasDesktop = false;
      return;
    }
    /* Manual/editor mode keeps the configured introduction accessible by arrows;
       desktop stays collapsed unless default-open is requested (applied on reveal). */
    if (!desktop && inst.active < 0 && inst.count > 0) setActive(inst, hasMobileIntro(inst) ? -1 : 0);
    else if (desktop && inst.wasDesktop === false && inst.active >= 0) setActive(inst, -1);
    else redrawActive(inst);
    inst.wasDesktop = desktop;
  }

  /* standard + desktop + default-open opt-in: auto-open feature 0 once the section
     reveals (called from scheduleRevealRedraw, so it opens after the entrance settles
     instead of at init). No-op in hotspots mode, on mobile, or if already open. */
  function maybeDefaultOpen(inst) {
    if (inst.config.mode !== 'standard' || !inst.config.defaultOpen) return;
    if (!window.matchMedia(DESKTOP_MQ).matches) return;
    if (inst.active < 0 && inst.count > 0) setActive(inst, 0, true);
  }

  /* ---------- state ---------------------------------------------------- */
  function setActive(inst, index, defer) {
    if (inst.active !== index && inst.scene) inst.scene.scrollTop = 0;
    inst.active = index;
    inst.root.classList.toggle('is-neutral', hasMobileIntro(inst) && index < 0);

    /* exclusive .is-active on every callout / card / dot for this index */
    var all = inst.root.querySelectorAll('[data-feature-index]');
    for (var i = 0; i < all.length; i++) {
      var idx = parseInt(all[i].getAttribute('data-feature-index'), 10);
      var on = idx === index;
      all[i].classList.toggle('is-active', on);
      if (all[i].hasAttribute('aria-expanded')) all[i].setAttribute('aria-expanded', on ? 'true' : 'false');
      if (all[i].hasAttribute('data-hotspot-card')) all[i].setAttribute('aria-hidden', on ? 'false' : 'true');
      if (all[i].className && String(all[i].className).indexOf('__pgdot') !== -1) {
        if (on) all[i].setAttribute('aria-current', 'true');
        else all[i].removeAttribute('aria-current');
      }
    }

    /* image swap (per visible layout) + mobile track position */
    var stages = inst.root.querySelectorAll('[data-hotspot-stage]');
    for (var s = 0; s < stages.length; s++) swapImage(inst, stages[s], index);

    var track = inst.root.querySelector('[data-hotspot-track]');
    if (track) {
      /* each step shifts by one card (100%) PLUS the inter-slide gap (px) */
      var slideGap = parseFloat(track.getAttribute('data-gap')) || 0;
      var trackIndex = Math.max(0, index);
      track.style.transform = 'translateX(calc(' + (-trackIndex * 100) + '% - ' + (trackIndex * slideGap) + 'px))';
    }

    /* mobile shows one terminus dot — move it to the active feature's hotspot */
    if (index >= 0) {
      var term = inst.root.querySelector('[data-hotspot-terminus]');
      var card = inst.root.querySelector('[data-hotspot-card="' + index + '"]');
      if (term && card) {
        term.style.left = card.getAttribute('data-hx') + '%';
        term.style.top = card.getAttribute('data-hy') + '%';
      }
    }

    /* A USER open/switch (defer=true) waits out its layout transition before the
       animated stroke draws — desktop panel expand OR mobile slider slide (see
       settleThenDraw) — so the line draws once to the settled target. Collapse
       (index < 0), reduced-motion, and the init/reveal activation (defer falsy,
       held by the settling guard until scheduleRevealRedraw) draw immediately. */
    if (index >= 0 && !prefersReducedMotion() && defer) {
      settleThenDraw(inst, index);
    } else {
      redrawActive(inst, true);   /* a user switch draws the line on; resize/settle redraws don't */
    }

    var prevButton = inst.root.querySelector('[class*="__arrow--prev"]');
    var nextButton = inst.root.querySelector('[class*="__arrow--next"]');
    var hasNeutralPhase = hasMobileIntro(inst);
    if (prevButton) prevButton.disabled = hasNeutralPhase ? index < 0 : index <= 0;
    if (nextButton) nextButton.disabled = index >= inst.count - 1;
  }

  function swapImage(inst, stage, index) {
    var imgs = stage.querySelectorAll('[data-hotspot-img]');
    var feat = index >= 0 ? inst.root.querySelector('[data-feature-index="' + index + '"][data-has-image="true"]') : null;
    var target = feat ? index.toString() : 'main';
    var targetImage = null;
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].getAttribute('data-hotspot-img') === target) targetImage = imgs[i];
    }
    if (!targetImage) return;

    var token = (stage._slImageToken || 0) + 1;
    stage._slImageToken = token;
    var image = targetImage.querySelector('img');

    function commitSwap() {
      if (stage._slImageToken !== token || inst.active !== index) return;
      syncFeatureImageSize(inst, target === 'main' ? -1 : index, target === 'main' ? null : targetImage);
      for (var layerIndex = 0; layerIndex < imgs.length; layerIndex++) {
        var selected = imgs[layerIndex] === targetImage;
        imgs[layerIndex].style.zIndex = selected ? '2' : '1';
        imgs[layerIndex].style.opacity = selected ? '1' : '0';
      }
      requestAnimationFrame(function () { redrawActive(inst, false); });
    }

    /* Keep the previous layer fully visible until the replacement is decoded.
       This removes the blank frame on the first lazy-loaded feature. */
    if (image && !image.complete) {
      image.addEventListener('load', commitSwap, { once: true });
      image.addEventListener('error', function () {
        if (stage._slImageToken !== token || inst.active !== index) return;
        target = 'main';
        targetImage = stage.querySelector('[data-hotspot-img="main"]');
        commitSwap();
      }, { once: true });
      return;
    }
    if (image && image.decode) {
      image.decode().then(commitSwap, commitSwap);
    } else {
      commitSwap();
    }
  }

  function syncFeatureImageSize(inst, index, imageLayer) {
    var featureHasImage = index >= 0 && inst.root.querySelector(
      '[data-feature-index="' + index + '"][data-has-image="true"]'
    );
    var layer = imageLayer;
    if (featureHasImage && !layer) {
      layer = inst.root.querySelector('[data-hotspot-img="' + index + '"]');
    }

    var height = layer && parseFloat(layer.getAttribute(
      window.matchMedia(DESKTOP_MQ).matches
        ? 'data-image-height-desktop'
        : 'data-image-height-mobile'
    ));
    if (featureHasImage && height > 0) {
      inst.root.style.setProperty('--sl-active-img-h', height + 'px');
      var image = layer.querySelector('img');
      var width = image && (image.naturalWidth || Number(image.getAttribute('width')));
      var imageHeight = image && (image.naturalHeight || Number(image.getAttribute('height')));
      if (width > 0 && imageHeight > 0) inst.root.style.setProperty('--sl-active-img-ar', String(width / imageHeight));
      else inst.root.style.removeProperty('--sl-active-img-ar');
      return;
    }

    inst.root.style.removeProperty('--sl-active-img-h');
    inst.root.style.removeProperty('--sl-active-img-ar');
  }

  /* ---------- public actions ------------------------------------------- */
  function activate(sectionId, index) {
    var inst = instances[sectionId];
    if (!inst) return;
    index = parseInt(index, 10);
    if (isNaN(index)) return;
    scrollToFeature(inst, index);
  }

  function toggle(sectionId, index) {           /* desktop accordion click */
    var inst = instances[sectionId];
    if (!inst) return;
    index = parseInt(index, 10);
    setActive(inst, inst.active === index ? -1 : index, true);
  }

  function step(sectionId, dir) {               /* mobile arrows — finite */
    var inst = instances[sectionId];
    if (!inst || !inst.count) return;
    if (hasMobileIntro(inst)) {
      var scrollTarget = inst.active < 0 ? (dir > 0 ? 0 : -1) : inst.active + dir;
      scrollToFeature(inst, Math.max(-1, Math.min(inst.count - 1, scrollTarget)));
      return;
    }
    var next = inst.active < 0 ? 0 : Math.max(0, Math.min(inst.count - 1, inst.active + dir));
    setActive(inst, next, true);
  }
  function next(sectionId) { step(sectionId, 1); }
  function prev(sectionId) { step(sectionId, -1); }

  /* ---------- init / teardown ----------------------------------------- */
  function teardown(inst) {
    if (inst.io) inst.io.disconnect();
    if (inst.ro) inst.ro.disconnect();
    if (inst.revealTimer) { clearTimeout(inst.revealTimer); inst.revealTimer = null; }
    if (inst.settleTimer) { clearTimeout(inst.settleTimer); inst.settleTimer = null; }
    if (inst.dotTimer) { clearTimeout(inst.dotTimer); inst.dotTimer = null; }
    inst.settleGen = (inst.settleGen || 0) + 1;   /* cancel any pending settle finish */
    inst.settling = false;
    if (inst.scene && inst.onStepWheel) inst.scene.removeEventListener('wheel', inst.onStepWheel);
    if (inst.scene && inst.onStepTouchStart) inst.scene.removeEventListener('touchstart', inst.onStepTouchStart);
    if (inst.scene && inst.onStepTouchMove) inst.scene.removeEventListener('touchmove', inst.onStepTouchMove);
    if (inst.scene && inst.onStepTouchEnd) {
      inst.scene.removeEventListener('touchend', inst.onStepTouchEnd);
      inst.scene.removeEventListener('touchcancel', inst.onStepTouchEnd);
    }
    clearTimeout(inst.wheelTimer);
    clearMobilePin(inst);
    inst.root.classList.remove('is-scroll-ready');
  }

  /* The entrance reveal is a pure CSS transition (opacity/transform) — the
     ResizeObserver can't see it, so a connector drawn DURING the reveal can end
     up stale: notably on mobile, where a feature is active from load and the card
     + image-stage settle at staggered times, leaving the first line pointing off
     into space until the visitor clicks. Redraw once (non-animated) after the
     reveal has fully settled. Desktop is unaffected (it loads collapsed, active
     = -1, so no line is drawn until a click). */
  function scheduleRevealRedraw(inst) {
    var r = (inst.config && inst.config.reveal) || {};
    var settle = (r.enabled === false || inst.inEditor)
      ? 0
      : (r.delay || 0) + (r.duration || 600) + ((inst.count || 0) * (r.stagger || 0)) + 80;
    if (inst.revealTimer) clearTimeout(inst.revealTimer);
    inst.revealTimer = setTimeout(function () {
      inst.settling = false;    /* layout has settled — release the draw hold */
      redrawActive(inst, false);
      maybeDefaultOpen(inst);   /* desktop standard default-open opens AFTER the entrance */
    }, settle);
  }

  function init(sectionId, config) {
    var rootEl = root(sectionId);
    if (!rootEl) return;

    if (instances[sectionId]) teardown(instances[sectionId]);   /* idempotent re-init */

    var inst = {
      root: rootEl,
      config: {
        lineSpeed: (config && config.lineSpeed) || 1000,
        lineClearance: (config && config.lineClearance != null) ? config.lineClearance : 16,
        showConnectors: !config || config.showConnectors !== false,
        textAlign: (config && config.textAlign) || 'left',
        imageMotion: (config && config.imageMotion) || 'none',
        mode: (config && config.mode) || 'standard',
        defaultOpen: !!(config && config.defaultOpen),
        reveal: (config && config.reveal) || {}
      },
      active: -1,
      inEditor: !!(window.Shopify && window.Shopify.designMode),
      scene: rootEl.querySelector('[data-hotspot-scene]'),
      footer: rootEl.querySelector('[data-hotspot-footer]'),
      count: rootEl.querySelectorAll('[data-hotspot-callout]').length ||
             rootEl.querySelectorAll('[data-hotspot-card]').length
    };
    instances[sectionId] = inst;
    var mobile = rootEl.querySelector('[data-hotspot-mobile]');
    if (mobile && mobile.getAttribute('data-arrows') === 'sides') {
      var navigation = mobile.querySelector('[class*="__mnav"]');
      var mobileStage = mobile.querySelector('[data-hotspot-stage="mobile"]');
      if (navigation && mobileStage) mobileStage.appendChild(navigation);
    }
    syncFooterHeight(inst);
    bindMobileStepLock(inst);

    /* Hold every connector draw until the entrance reveal settles. The first
       paint runs against un-laid-out geometry (especially mobile, where a feature
       is active from load and the card/stage settle at staggered times), which
       left the line pointing "from beyond" until the visitor tapped. With the hold
       on, scheduleRevealRedraw clears it and draws once, against settled geometry. */
    inst.settling = true;

    /* entrance reveal — fire once, then disconnect (does NOT auto-open).
       In the Theme Editor the section re-renders on every setting change, which
       resets it to the hidden pre-reveal state; a fresh IntersectionObserver on
       an already-scrolled section can't be relied on to re-fire, so the operator
       loses sight of what they're editing until a full save. Skip the gate
       entirely in designMode — the editor must always show the section. */
    if (!inst.inEditor && 'IntersectionObserver' in window && !rootEl.classList.contains('is-revealed')) {
      inst.io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            rootEl.classList.add('is-revealed');
            teardownIO(inst);
            scheduleRevealRedraw(inst);
            break;
          }
        }
      }, { threshold: mobileScrollEnabled(inst) ? 0.01 : 0.2 });
      inst.io.observe(rootEl);
    } else {
      rootEl.classList.add('is-revealed');
      scheduleRevealRedraw(inst);
    }

    /* keep the active line glued to its dot through resize / image reflow */
    if ('ResizeObserver' in window) {
      /* observe stages AND columns/body: opening a callout grows its column and —
         with vertical centering — re-centers it, moving the active title; the
         connector must re-track to it. These redraws are non-animated (reposition). */
      inst.ro = new ResizeObserver(function () {
        syncFooterHeight(inst);
        inst.scrollMetrics = null;
        redrawActive(inst, false);
      });
      var observed = rootEl.querySelectorAll('[data-hotspot-footer], [data-hotspot-scene], [data-hotspot-stage], [data-hotspot-imgbox], [data-hotspot-col], [data-hotspot-body], [class*="__header"], [class*="__mcallouts"]');
      for (var s = 0; s < observed.length; s++) inst.ro.observe(observed[s]);
      document.querySelectorAll('product-sticky-form[data-show-outside-section]').forEach(function (form) {
        inst.ro.observe(form);
      });
    }

    /* final safety net: a late image decode can shift the stage after the reveal
       has settled — redraw the active line once everything has loaded. */
    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () {
        inst.scrollMetrics = null;
        redrawActive(inst, false);
      }, { once: true });
    }

    /* GSAP is OPTIONAL — only used to randomise collage jitter, never loaded */
    if (inst.config.imageMotion === 'collage' && !prefersReducedMotion() && window.gsap) {
      randomiseCollage(rootEl, window.gsap);
    }

    syncBreakpoint(inst);   /* mobile → activate feature 0; desktop → stay collapsed */
  }

  function teardownIO(inst) { if (inst.io) { inst.io.disconnect(); inst.io = null; } }

  function randomiseCollage(rootEl, G) {
    var frames = rootEl.querySelectorAll('[data-hotspot-stage] [data-hotspot-img="main"]');
    for (var i = 0; i < frames.length; i++) {
      frames[i].style.animation = 'none';          /* hand off from the CSS keyframes */
      var rot = (Math.random() * 2 - 1) * 1.4;     /* ±1.4deg */
      var dx = (Math.random() * 2 - 1) * 6;
      G.fromTo(frames[i],
        { x: dx, y: -10, rotation: rot, opacity: 0 },
        { x: 0, y: 0, rotation: rot * 0.3, opacity: 1, duration: 0.7, ease: 'steps(6)' });
    }
  }

  /* ---------- Theme Editor (designMode) ------------------------------- */
  function featureIndexFromBlock(rootEl, blockId) {
    var el = rootEl.querySelector('[data-block-id="' + blockId + '"]');
    return el ? parseInt(el.getAttribute('data-feature-index'), 10) : -1;
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:block:select', function (e) {
      var inst = findInst(e.target);
      if (!inst) return;
      var idx = featureIndexFromBlock(inst.root, e.detail && e.detail.blockId);
      if (idx >= 0) setActive(inst, idx, true);   /* editor select = user open → defer */
    });
    document.addEventListener('shopify:block:deselect', function (e) {
      var inst = findInst(e.target);
      if (inst) setActive(inst, -1);            /* back to the clean state */
    });
  }

  function findInst(node) {
    var rootEl = node && node.closest ? node.closest('[id^="sl-section-"]') : null;
    if (!rootEl) return null;
    var id = rootEl.id.replace('sl-section-', '');
    return instances[id] || null;
  }

  /* re-init on section (re)load in the editor */
  document.addEventListener('shopify:section:load', function (e) {
    var rootEl = e.target.matches && e.target.matches('[data-sl-hotspot]')
      ? e.target
      : (e.target.querySelector ? e.target.querySelector('[data-sl-hotspot]') : null);
    if (rootEl) {
      var id = rootEl.id.replace('sl-section-', '');
      /* config travels via the section's own inline bootstrap, which re-runs
         on load; this listener just guarantees stale observers are dropped. */
      if (instances[id] && instances[id].root !== rootEl) {
        teardown(instances[id]);
        delete instances[id];
      }
    }
  });

  /* A deleted section has no following load event. Release its observers and
     timers on unload so repeated Theme Editor edits cannot retain old DOM. */
  document.addEventListener('shopify:section:unload', function (e) {
    var rootEl = e.target.matches && e.target.matches('[data-sl-hotspot]')
      ? e.target
      : (e.target.querySelector ? e.target.querySelector('[data-sl-hotspot]') : null);
    if (!rootEl) return;
    var id = rootEl.id.replace('sl-section-', '');
    if (!instances[id]) return;
    teardown(instances[id]);
    delete instances[id];
  });

  /* redraw on viewport resize (breakpoint swap desktop ↔ mobile) */
  var rafPending = false;
  window.addEventListener('resize', function () {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () {
      rafPending = false;
      for (var id in instances) if (instances.hasOwnProperty(id)) syncBreakpoint(instances[id]);
    });
  });
  window.addEventListener('scroll', queueScrollSync, { passive: true });

  /* ---------- publish + drain the bootstrap queue --------------------- */
  var API = { _q: [], init: init, activate: activate, toggle: toggle, next: next, prev: prev };
  window.slHotspot = API;
  queued.forEach(function (call) { if (API[call[0]]) API[call[0]].apply(API, call.slice(1)); });
})();
